/**
 * Schémas JSON-LD pour le SEO Unotif
 * Ces schémas améliorent l'éligibilité aux rich snippets Google
 */

// Constantes
const BASE_URL = "https://unotif.com";
const LOGO_URL = "https://storage.googleapis.com/gpt-engineer-file-uploads/v8WsvNvgs0ZlR2L5Amdm9TPdyqx2/social-images/social-1768482845695-Couverture%20Unotif.png";
const DEFAULT_DESCRIPTION = "Créez des cartes Wallet mobiles et envoyez des notifications push visibles sur l'écran verrouillé. Sans application, sans SMS, sans email.";

// Schema Organization - Identifie Unotif comme organisation
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Unotif",
  url: BASE_URL,
  logo: LOGO_URL,
  sameAs: [
    "https://www.linkedin.com/company/unotif/",
    "https://www.facebook.com/groups/unotif",
    "https://www.youtube.com/@unotif"
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    url: `${BASE_URL}/contact`
  }
};

// Schema WebSite - Décrit le site pour les sitelinks
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Unotif",
  alternateName: ["UNOTIF", "Unotif.com"],
  url: BASE_URL,
  description: DEFAULT_DESCRIPTION,
  publisher: {
    "@type": "Organization",
    name: "Unotif"
  }
};

// Générateur WebPage - Contextualise chaque page
export function generateWebPageSchema(title: string, description: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description: description,
    url: `${BASE_URL}${path}`,
    isPartOf: {
      "@type": "WebSite",
      name: "Unotif",
      url: BASE_URL
    }
  };
}

// Générateur Product + Offer - Pour les rich snippets avec prix
export function generateProductSchema(
  name: string, 
  description: string, 
  price: number,
  priceValidUntil?: string
) {
  // Date de validité par défaut : fin de l'année en cours + 1
  const validUntil = priceValidUntil || `${new Date().getFullYear() + 1}-12-31`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `Unotif ${name}`,
    description: description,
    image: LOGO_URL,
    brand: {
      "@type": "Organization",
      name: "Unotif"
    },
    offers: {
      "@type": "Offer",
      price: price.toString(),
      priceCurrency: "EUR",
      priceValidUntil: validUntil,
      availability: "https://schema.org/InStock",
      url: `${BASE_URL}/tarifs`
    }
  };
}

// Générateur FAQPage - Pour les rich snippets FAQ
export function generateFAQSchema(items: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
}

// Générateur BreadcrumbList - Pour la navigation enrichie dans les SERPs
export function generateBreadcrumbSchema(
  items: Array<{ name: string; path: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.path}`
    }))
  };
}

// Générateur Article - Pour les rich snippets d'articles de blog
export function generateArticleSchema(
  headline: string,
  description: string,
  url: string,
  datePublished: string,
  dateModified?: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: headline,
    description: description,
    image: LOGO_URL,
    url: url,
    datePublished: datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Organization",
      name: "Unotif",
      url: BASE_URL
    },
    publisher: {
      "@type": "Organization",
      name: "Unotif",
      logo: {
        "@type": "ImageObject",
        url: LOGO_URL
      }
    }
  };
}

// Schema SoftwareApplication - Décrit Unotif comme application SaaS
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Unotif",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: DEFAULT_DESCRIPTION,
  url: BASE_URL,
  offers: [
    {
      "@type": "Offer",
      name: "Gratuit",
      price: "0",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`
    },
    {
      "@type": "Offer",
      name: "Starter",
      price: "29",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`
    },
    {
      "@type": "Offer",
      name: "Pro",
      price: "89",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`
    }
  ],
  publisher: {
    "@type": "Organization",
    name: "Unotif",
    url: BASE_URL
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "50",
    bestRating: "5",
    worstRating: "1"
  },
  featureList: [
    "Cartes Wallet Apple & Google",
    "Notifications push sur écran verrouillé",
    "Notifications géolocalisées",
    "Segmentation par tags",
    "Statistiques détaillées",
    "Code d'intégration embed"
  ]
};
